/* eslint-disable */

export interface SignalsContext {
  /**
   * Touch event information from the client to be used for risk analysis.
   * Only sent by android.
   */
  touch_events?: TouchSignalEvent[];
  /** Is the device in the middle of an active phone call. */
  phoneCallActive?: boolean;
  /** Is the device's screen being recorded or captured by another app. */
  screenCaptured?: boolean;
}

/**
 * Effectively, a proto version of Android's motion event:
 * https://developer.android.com/reference/android/view/MotionEvent
 */
export interface TouchSignalEvent {
  time?: number;
  device_id?: number;
  action_value?: number;
  edge_flag?: number;
  meta_state?: number;
  flags?: number;
  button_states?: number;
  pointers?: TouchSignalPointerList[];
}

export interface TouchSignalPointerList {
  pointers?: TouchSignalPointerListTouchSignalPointer[];
}

export interface TouchSignalPointerListTouchSignalPointer {
  x?: number;
  y?: number;
  pressure?: number;
  size?: number;
  tool_type?: number;
  touch_major?: number;
  touch_minor?: number;
  tool_major?: number;
  tool_minor?: number;
  orientation?: number;
}

/**
 * The App Attest Attestation API is Apple's anti-abuse API that allows app developers
 * to assess the app running on a physical iOS device isn't tampered.
 * See https://developer.apple.com/documentation/devicecheck/establishing_your_app_s_integrity
 * for more details.
 */
export interface AppAttestAttestation {
  /**
   * The CBOR attestation object encoded as a base64 string.
   * See https://developer.apple.com/documentation/devicecheck/validating_apps_that_connect_to_your_server
   * for more details.
   */
  payload?: string;
}

/**
 * The SafetyNet Attestation API is Android’s anti-abuse API that allows app developers
 * to assess the Android device their app is running on.
 * See https://developer.android.com/training/safetynet/attestation.html for more details.
 *
 * @deprecated
 */
export interface SafetyNetAttestation {
  /** The nonce used to sign this attestation response */
  nonce?: string;
  /** SafetyNet attestation JWS object, this is a JSON document that's signed with the above nonce */
  jws_result?: string;
  /** The Google Play Services connection result status code. */
  play_service_status_code?: SafetyNetAttestationGoogleConnectionResult;
  /** Attestation status */
  status?: SafetyNetAttestationStatus;
}

/**
 * Describes the status of the attestation process the client executed. Replaces
 * prior separate Play Services and SafetyNet status codes.
 */
export enum SafetyNetAttestationStatus {
  DO_NOT_USE = "DO_NOT_USE",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
}

/**
 * Proto representing GoogleClientAPI ConnectionResult codes:
 * https://developers.google.com/android/reference/com/google/android/gms/common/ConnectionResult.html
 */
export enum SafetyNetAttestationGoogleConnectionResult {
  API_UNAVAILABLE = "API_UNAVAILABLE",
  CANCELED = "CANCELED",
  DEVELOPER_ERROR = "DEVELOPER_ERROR",
  DRIVE_EXTERNAL_STORAGE_REQUIRED = "DRIVE_EXTERNAL_STORAGE_REQUIRED",
  INTERNAL_ERROR = "INTERNAL_ERROR",
  INTERRUPTED = "INTERRUPTED",
  INVALID_ACCOUNT = "INVALID_ACCOUNT",
  LICENSE_CHECK_FAILED = "LICENSE_CHECK_FAILED",
  NETWORK_ERROR = "NETWORK_ERROR",
  RESOLUTION_REQUIRED = "RESOLUTION_REQUIRED",
  RESTRICTED_PROFILE = "RESTRICTED_PROFILE",
  SERVICE_DISABLED = "SERVICE_DISABLED",
  SERVICE_INVALID = "SERVICE_INVALID",
  SERVICE_MISSING = "SERVICE_MISSING",
  SERVICE_MISSING_PERMISSION = "SERVICE_MISSING_PERMISSION",
  SERVICE_UPDATING = "SERVICE_UPDATING",
  SERVICE_VERSION_UPDATE_REQUIRED = "SERVICE_VERSION_UPDATE_REQUIRED",
  SIGN_IN_FAILED = "SIGN_IN_FAILED",
  SIGN_IN_REQUIRED = "SIGN_IN_REQUIRED",
  SUCCESS = "SUCCESS",
  TIMEOUT = "TIMEOUT",
}

/**
 * The Play Integrity API is Android’s anti-abuse API that allows app developers
 * to assess the Android device their app is running on.
 * See https://developer.android.com/google/play/integrity/overview for more details.
 */
export interface PlayIntegrityAttestation {
  /** The nonce used to sign this attestation response. */
  nonce?: string;
  /** Play Integrity response token, that's signed with the above nonce. */
  token?: string;
  /** The Google Play Services connection result status code. */
  play_service_status_code?: PlayIntegrityAttestationGoogleConnectionResult;
  /** Attestation status. */
  status?: PlayIntegrityAttestationStatus;
  /** Error code if the integrity check failed. */
  integrity_error_code?: PlayIntegrityAttestationIntegrityErrorCode;
}

/** Describes the status of the attestation process the client executed. */
export enum PlayIntegrityAttestationStatus {
  DO_NOT_USE = "DO_NOT_USE",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
}

/**
 * Proto representing GoogleClientAPI ConnectionResult codes:
 * https://developers.google.com/android/reference/com/google/android/gms/common/ConnectionResult.html
 */
export enum PlayIntegrityAttestationGoogleConnectionResult {
  API_UNAVAILABLE = "API_UNAVAILABLE",
  CANCELED = "CANCELED",
  DEVELOPER_ERROR = "DEVELOPER_ERROR",
  DRIVE_EXTERNAL_STORAGE_REQUIRED = "DRIVE_EXTERNAL_STORAGE_REQUIRED",
  /** GOOGLE_CONNECTION_RESULT_INTERNAL_ERROR - Prefixed with enum name to avoid clash with IntegrityErrorCode value with the same name. */
  GOOGLE_CONNECTION_RESULT_INTERNAL_ERROR = "GOOGLE_CONNECTION_RESULT_INTERNAL_ERROR",
  INTERRUPTED = "INTERRUPTED",
  INVALID_ACCOUNT = "INVALID_ACCOUNT",
  LICENSE_CHECK_FAILED = "LICENSE_CHECK_FAILED",
  /** GOOGLE_CONNECTION_RESULT_NETWORK_ERROR - Prefixed with enum name to avoid clash with IntegrityErrorCode value with the same name. */
  GOOGLE_CONNECTION_RESULT_NETWORK_ERROR = "GOOGLE_CONNECTION_RESULT_NETWORK_ERROR",
  RESOLUTION_REQUIRED = "RESOLUTION_REQUIRED",
  RESTRICTED_PROFILE = "RESTRICTED_PROFILE",
  SERVICE_DISABLED = "SERVICE_DISABLED",
  SERVICE_INVALID = "SERVICE_INVALID",
  SERVICE_MISSING = "SERVICE_MISSING",
  SERVICE_MISSING_PERMISSION = "SERVICE_MISSING_PERMISSION",
  SERVICE_UPDATING = "SERVICE_UPDATING",
  SERVICE_VERSION_UPDATE_REQUIRED = "SERVICE_VERSION_UPDATE_REQUIRED",
  SIGN_IN_FAILED = "SIGN_IN_FAILED",
  SIGN_IN_REQUIRED = "SIGN_IN_REQUIRED",
  SUCCESS = "SUCCESS",
  TIMEOUT = "TIMEOUT",
}

/**
 * Proto representing Play Integrity API error codes:
 * https://developer.android.com/google/play/integrity/reference/com/google/android/play/core/integrity/model/IntegrityErrorCode
 *
 * Tags in this enum map 1-to-1 to the unsigned values of the corresponding
 * constants in the Android type.
 */
export enum PlayIntegrityAttestationIntegrityErrorCode {
  NO_ERROR = "NO_ERROR",
  API_NOT_AVAILABLE = "API_NOT_AVAILABLE",
  PLAY_STORE_NOT_FOUND = "PLAY_STORE_NOT_FOUND",
  /** INTEGRITY_ERROR_CODE_NETWORK_ERROR - Prefixed with enum name to avoid clash with GoogleConnectionResult value with the same name. */
  INTEGRITY_ERROR_CODE_NETWORK_ERROR = "INTEGRITY_ERROR_CODE_NETWORK_ERROR",
  PLAY_STORE_ACCOUNT_NOT_FOUND = "PLAY_STORE_ACCOUNT_NOT_FOUND",
  APP_NOT_INSTALLED = "APP_NOT_INSTALLED",
  PLAY_SERVICES_NOT_FOUND = "PLAY_SERVICES_NOT_FOUND",
  APP_UID_MISMATCH = "APP_UID_MISMATCH",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  CANNOT_BIND_TO_SERVICE = "CANNOT_BIND_TO_SERVICE",
  NONCE_TOO_SHORT = "NONCE_TOO_SHORT",
  NONCE_TOO_LONG = "NONCE_TOO_LONG",
  GOOGLE_SERVER_UNAVAILABLE = "GOOGLE_SERVER_UNAVAILABLE",
  NONCE_IS_NOT_BASE64 = "NONCE_IS_NOT_BASE64",
  PLAY_STORE_VERSION_OUTDATED = "PLAY_STORE_VERSION_OUTDATED",
  PLAY_SERVICES_VERSION_OUTDATED = "PLAY_SERVICES_VERSION_OUTDATED",
  CLOUD_PROJECT_NUMBER_IS_INVALID = "CLOUD_PROJECT_NUMBER_IS_INVALID",
  CLIENT_TRANSIENT_ERROR = "CLIENT_TRANSIENT_ERROR",
  /** INTEGRITY_ERROR_CODE_INTERNAL_ERROR - Prefixed with enum name to avoid clash with GoogleConnectionResult value with the same name. */
  INTEGRITY_ERROR_CODE_INTERNAL_ERROR = "INTEGRITY_ERROR_CODE_INTERNAL_ERROR",
}
